import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ChevronIcon } from '@wbk/svg';
import { Link, useRouter } from '@wbk/config';
import { WebSection } from '@wbk/contentful/api';
import { ContentfulMedia, parseSectionItem } from '@wbk/contentful';
import { Button } from '@wbk/ui';
import ScrollTitle from '@/components/ui/ScrollTitle';
import { Carousel } from '@/components/ui/Carousel';
import { getEventRedirectUrl } from '@/utils/redirect';

type Props = {
  section: WebSection;
  className?: string;
  withoutTitle?: boolean;
};

const EventsFullScreenCarousel = ({ section, className, withoutTitle }: Props) => {
  const items = section.contentCollection.items;
  const { locale } = useRouter();
  const { t } = useTranslation('common');
  const seeMore = section?.webLinksCollection?.items?.[0];

  if (!items?.length) {
    return null;
  }

  return (
    <section className='container-padding container px-10 md:space-y-12'>
      <Carousel
        className='overflow-clip rounded-lg sm:mx-0'
        rootClassName='px-3 xl:px-0'
        options={{
          align: 'center',
          loop: true,
          slidesToScroll: 1,
          active: items.length > 1,
        }}
        autoplay={{
          delay: 3000,
          stopOnMouseEnter: true,
        }}
        arrowsClassName=''
        title={
          withoutTitle ? null : (
            <>
              <div className='flex items-end justify-between'>
                {section.title && <ScrollTitle title={section.title} />}

                {seeMore?.href && (
                  <Button
                    phill
                    as={Link}
                    href={seeMore.href}
                    target='_blank'
                    rel='noreferrer noopener'
                    className={twMerge(
                      '!text-body-XS sm:!text-body-S h-fit whitespace-nowrap border border-[#3885FF] bg-transparent px-4 py-1 capitalize text-white sm:px-6',
                      className
                    )}
                  >
                    {seeMore?.label || t('see_all')}
                    <ChevronIcon className='size-2 sm:size-3 rtl:rotate-180' />
                  </Button>
                )}
              </div>
            </>
          )
        }
        dots
      >
        {items.map((item) => {
          const parsedItem = parseSectionItem({ item, locale });
          if (!parsedItem) return null;

          const hasContent = parsedItem.title || parsedItem.subtitle || parsedItem.startingPrice;

          const redirectInfo = getEventRedirectUrl(
            item,
            'slug' in item ? item.slug : '',
            locale,
            parsedItem
          );

          return (
            <div
              className='group mx-auto flex h-48 w-full shrink-0 items-center justify-center sm:h-full'
              key={parsedItem.id}
            >
              {/* Slide Item */}
              <Button
                as={Link}
                href={redirectInfo.href}
                target={redirectInfo.isExternal ? '_blank' : undefined}
                queueProtected
                theme='transparent'
                shape='text'
                className='border-paper relative h-full basis-full flex-col justify-between rounded-none p-0 sm:rounded-3xl sm:border'
              >
                <div className='aspect-1 h-full w-full sm:aspect-[3/1]'>
                  {/* Desktop */}
                  <ContentfulMedia
                    media={parsedItem.image31}
                    poster={parsedItem.background?.url}
                    className='hidden h-full w-full overflow-hidden object-cover object-center transition-transform duration-300 ease-in-out group-hover:scale-110 sm:block'
                    ratio='3x1'
                    placeholder
                    controls
                  />
                  {/* Mobile */}
                  <ContentfulMedia
                    media={parsedItem.image11}
                    poster={parsedItem.background?.url}
                    className='h-full w-full overflow-hidden object-cover object-center sm:hidden'
                    width={800}
                    height={800}
                    fit='thumb'
                    ratio='1x1'
                    placeholder
                    controls
                  />

                  {/* Content */}
                  {hasContent && (
                    <>
                      <div className='absolute bottom-0 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 items-center text-center'>
                        <Button
                          phill
                          className='text-body-XS mx-auto w-fit justify-center gap-1 whitespace-nowrap py-1 font-semibold capitalize leading-none sm:px-4 sm:py-2 md:text-lg lg:mx-0'
                        >
                          {parsedItem.label}
                          <svg
                            width='21'
                            height='21'
                            viewBox='0 0 21 21'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              opacity='0.3'
                              d='M9.58313 2.1665C10.0434 2.1665 10.4165 2.5396 10.4165 2.99983C10.4165 3.46007 10.0434 3.83316 9.58313 3.83316H5.49982C4.57935 3.83316 3.83316 4.57935 3.83316 5.49982V15.4998C3.83316 16.4202 4.57935 17.1664 5.49982 17.1664H15.4998C16.4202 17.1664 17.1664 16.4202 17.1664 15.4998V13.8331C17.1664 13.3729 17.5395 12.9998 17.9998 12.9998C18.46 12.9998 18.8331 13.3729 18.8331 13.8331V15.4998C18.8331 17.3407 17.3407 18.8331 15.4998 18.8331H5.49982C3.65888 18.8331 2.1665 17.3407 2.1665 15.4998V5.49982C2.1665 3.65888 3.65888 2.1665 5.49982 2.1665H9.58313Z'
                              fill='#F4F4F5'
                            />
                            <path
                              d='M6.33725 12.081C6.33725 11.4454 6.20105 8.93705 8.17972 6.94703C9.51144 5.60775 11.5045 5.37545 13.888 5.26952V2.31368C13.888 1.66294 14.6599 1.32097 15.1419 1.75821L20.2553 6.39726C20.5811 6.69284 20.5838 7.20408 20.261 7.503L15.1476 12.2386C14.6674 12.6832 13.888 12.3427 13.888 11.6883V8.75621C12.3746 8.79782 11.2967 8.89408 10.3173 9.20566C8.42432 9.80788 7.30106 12.3902 6.99815 13.1768C6.94592 13.3125 6.81654 13.4051 6.67118 13.4051C6.48467 13.4051 6.33353 13.254 6.33411 13.0675C6.33519 12.7241 6.33725 12.3294 6.33725 12.081Z'
                              fill='white'
                            />
                          </svg>
                        </Button>
                      </div>

                      {/* Gradient overlay */}
                      <div className='absolute bottom-0 left-0 right-0 h-[80%] bg-gradient-to-t from-black/90 from-[10%] via-black/20 via-[70%] to-transparent to-[100%]' />
                    </>
                  )}
                </div>
              </Button>
            </div>
          );
        })}
      </Carousel>
    </section>
  );
};

export default EventsFullScreenCarousel;
